// ============================================================
// LEGAL & TAX VERIFICATION PAGE
// ============================================================

import React, { useState, useEffect } from 'react';
import { FileCheck, CheckCircle, XCircle, Clock, Download } from 'lucide-react';
import { Table, Badge, Button, SearchInput, Select, Pagination, Modal, StatCard, Alert } from '../components/ui';
import { usePagination, useSearch } from '../hooks';
import { getLegalVerifications } from '../services/mockData';
import { adminApi } from '../services/apiService';
import { formatDate, downloadCSV } from '../utils/helpers';
import { toast } from 'react-toastify';

const LegalVerification = () => {
  const [all, setAll] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selected, setSelected] = useState(null);
  const [statusFilter, setStatusFilter] = useState('');

  useEffect(() => {
    adminApi.getLegalVerifications(getLegalVerifications)
      .then(res => { setAll(Array.isArray(res) ? res : (res?.items || [])); setLoading(false); })
      .catch(e => { setError(e.message || 'Failed to load data'); setLoading(false); });
  }, []);

  const { query, setQuery, filtered } = useSearch(all, ['restaurantName', 'gstNumber', 'panNumber']);
  const final = statusFilter ? filtered.filter(l => l.status === statusFilter) : filtered;
  const { currentPage, totalPages, paginated, goTo } = usePagination(final);

  const approve = async (id) => {
    try { await adminApi.approveLegal(id); } catch { /* fallback */ }
    setAll(p => p.map(x => x.id === id ? { ...x, status: 'approved', reviewedAt: new Date().toISOString(), reviewedBy: 'Admin' } : x));
    toast.success('Verification approved!');
    setSelected(null);
  };

  const reject = async (id) => {
    try { await adminApi.rejectLegal(id, 'Documents invalid or expired'); } catch { /* fallback */ }
    setAll(p => p.map(x => x.id === id ? { ...x, status: 'rejected', remarks: 'Documents invalid or expired', reviewedAt: new Date().toISOString() } : x));
    toast.error('Verification rejected');
    setSelected(null);
  };

  const columns = [
    { key: 'id', label: 'Ref ID', render: v => <span style={{ fontFamily: 'monospace', fontWeight: 700 }}>{v}</span> },
    { key: 'restaurantName', label: 'Restaurant', render: v => <b>{v}</b> },
    { key: 'gstNumber', label: 'GST Number', render: v => <span style={{ fontFamily: 'monospace', fontSize: 12 }}>{v}</span> },
    { key: 'licenseNumber', label: 'License No.', render: v => <span style={{ fontFamily: 'monospace', fontSize: 12 }}>{v}</span> },
    { key: 'status', label: 'Status', render: v => <Badge status={v} /> },
    { key: 'submittedAt', label: 'Submitted', render: v => formatDate(v) },
    { key: 'reviewedBy', label: 'Reviewed By', render: v => v || '—' },
    {
      key: 'id', label: 'Actions',
      render: (_, row) => (
        <div style={{ display: 'flex', gap: 6 }}>
          <Button variant="info" size="sm" onClick={() => setSelected(row)}>Review</Button>
          {row.status === 'pending' && <>
            <Button variant="success" size="sm" onClick={() => approve(row.id)}>Approve</Button>
            <Button variant="danger" size="sm" onClick={() => reject(row.id)}>Reject</Button>
          </>}
        </div>
      ),
    },
  ];

  return (
    <div>
      <div className="admin-page-header">
        <div>
          <h1 className="admin-page-title">Legal & Tax Verification</h1>
          <p className="admin-page-subtitle">Review GST, PAN, and license documents</p>
        </div>
        <Button variant="secondary" icon={<Download size={14} />} onClick={() => downloadCSV(all, 'legal')}>Export</Button>
      </div>

      <div className="admin-stat-grid" style={{ gridTemplateColumns: 'repeat(4, 1fr)', marginBottom: 20 }}>
        <StatCard label="Total" value={all.length} icon={<FileCheck size={18} />} color="#3B82F6" />
        <StatCard label="Pending" value={all.filter(l => l.status === 'pending').length} icon={<Clock size={18} />} color="#F59E0B" />
        <StatCard label="Approved" value={all.filter(l => l.status === 'approved').length} icon={<CheckCircle size={18} />} color="#10B981" />
        <StatCard label="Rejected" value={all.filter(l => l.status === 'rejected').length} icon={<XCircle size={18} />} color="#EF4444" />
      </div>

      <div className="admin-table-wrapper">
        <div className="admin-table-toolbar">
          <SearchInput value={query} onChange={setQuery} placeholder="Search verifications..." />
          <Select value={statusFilter} onChange={setStatusFilter} placeholder="All Status"
            options={[{ value: 'pending', label: 'Pending' }, { value: 'approved', label: 'Approved' }, { value: 'rejected', label: 'Rejected' }]} />
        </div>
        <Table columns={columns} data={paginated} loading={loading} />
        <Pagination currentPage={currentPage} totalPages={totalPages} totalItems={final.length} itemsPerPage={10} onPageChange={goTo} />
      </div>

      <Modal open={!!selected} onClose={() => setSelected(null)} title="Verification Details" size="md"
        footer={<>
          <Button variant="secondary" onClick={() => setSelected(null)}>Close</Button>
          {selected?.status === 'pending' && <>
            <Button variant="success" onClick={() => approve(selected.id)}>Approve</Button>
            <Button variant="danger" onClick={() => reject(selected.id)}>Reject</Button>
          </>}
        </>}>
        {selected && (
          <div>
            <div style={{ marginBottom: 16 }}><Badge status={selected.status} /></div>
            {selected.remarks && <Alert type="danger">{selected.remarks}</Alert>}
            {[
              ['Restaurant', selected.restaurantName], ['Reference ID', selected.id],
              ['GST Number', selected.gstNumber], ['PAN Number', selected.panNumber],
              ['License Number', selected.licenseNumber], ['Submitted', formatDate(selected.submittedAt)],
              ['Reviewed By', selected.reviewedBy || 'Pending'], ['Reviewed At', selected.reviewedAt ? formatDate(selected.reviewedAt) : 'Pending'],
            ].map(([l, v]) => (
              <div key={l} className="admin-info-row">
                <span className="admin-info-row-label">{l}</span>
                <span className="admin-info-row-value">{v}</span>
              </div>
            ))}
            <div className="admin-alert admin-alert-info" style={{ marginTop: 16 }}>
              <FileCheck size={16} /> Documents would be shown here in production with preview functionality.
            </div>
          </div>
        )}
      </Modal>
    </div>
  );
};

export default LegalVerification;
